import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ExportDocument from '@Application/Beneficiaries/Documents/Commands/ExportDocument';
import exportDocument from '@Application/Beneficiaries/Documents/exportDocument';
import { PermissionEnum } from '@Application/Security/Roles';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import DocumentExportWarning from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/DocumentVerificationHeader/DocumentExportWarning';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import type DocumentView from '@UI/Store/Documents/Views/DocumentView';
import { Download, TriangleAlert } from 'lucide-react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button, ConfirmDialog } from '@verclam/ui-kit';

type Props = {
	readonly subject: SubjectAddress;
	readonly document: DocumentView;
};

/**
 * "Télécharger" for one row of a document list: assembles the document's saved content into a `.docx`
 * in the browser (`exportDocument`). The confirmation appears only when the row's own
 * progress carries not-addressed or still-to-verify information, the same rule as the verification
 * screen's header.
 */
const DownloadDocumentButton: React.FC<Props> = ({ subject, document }) => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const hasPermission = useHasPermission(PermissionEnum.DOCUMENT_EXPORT);

	const [isConfirmVisible, setIsConfirmVisible] = React.useState(false);
	const [isExporting, setIsExporting] = React.useState(false);

	const notAddressedCount =
		document.progress.byStatus.find((entry) => entry.status.value === 'not_addressed')?.count ?? 0;
	const remainingCount = document.progress.remaining;

	const handleHideConfirm = React.useCallback(() => {
		setIsConfirmVisible(false);
	}, []);

	const handleStartExport = React.useCallback(() => {
		setIsExporting(true);
		dispatch(exportDocument(new ExportDocument(facilityId, subject, document.id)))
			.catch(ignoreHandledError)
			.finally(() => {
				setIsExporting(false);
				setIsConfirmVisible(false);
			});
	}, [dispatch, facilityId, subject, document.id]);

	const handleClick = React.useCallback(() => {
		if (notAddressedCount > 0 || remainingCount > 0) {
			setIsConfirmVisible(true);
			return;
		}

		handleStartExport();
	}, [notAddressedCount, remainingCount, handleStartExport]);

	if (!hasPermission) {
		return null;
	}

	return (
		<React.Fragment>
			<Button
				type="button"
				size="sm"
				severity="secondary"
				outlined
				label="Télécharger"
				icon={<Download size={16} />}
				loading={isExporting}
				onClick={handleClick}
			/>
			<ConfirmDialog
				visible={isConfirmVisible}
				onHide={handleHideConfirm}
				header="Télécharger ce document ?"
				icon={<TriangleAlert size={24} aria-hidden="true" />}
				message={
					<DocumentExportWarning notAddressed={notAddressedCount} remaining={remainingCount} />
				}
				acceptLabel="Télécharger"
				rejectLabel="Annuler"
				accept={handleStartExport}
				reject={handleHideConfirm}
				loading={isExporting}
			/>
		</React.Fragment>
	);
};

export default DownloadDocumentButton;
